export interface ISentiment {
	entry: number;
	negative: number;
	neutral?: number;
	positive: number;
	text: string;
	negativeColor?: string;
	neutralColor?: string;
	positiveColor?: string;
}

export interface Video {
	_id: string;
	metaData: VideoMetaData;
	content: VideoContent[];
	creatorData?: any[];
}

export interface VideoMetaData {
	id: string;
	creatorId: string;
	creatorName?: string;
	creatorSub?: string;
	creatorUrl?: string;
	contentType: string;
	duration: string;
	stats: VideoMetaDataStats;
	title: string;
	description: string;
	date: string;
	thumbnail: string;
	url: string;
	platform: string;
	hashtags: string[];
	brandsMentioned: string[];
	productsMentioned: string[];
	iabClassification: string[];
	sponsored: boolean;
	sponsors?: any[];
	keyWords?: string[];
	entities?: string[];
	topics?: string[];
	links?: string[];
	summary: string;
	comprehension: number;
	text: string;
	searchTranscription?: string;
	searchTerm?: string;
	segments: number;
	SentimentSummary?: {
		neg: number;
		neu: number;
		pos: number;
	};
}

export interface VideoMetaDataStats {
	views: number;
	likes: number;
	dislikes: number;
	comments: number;
}

export interface VideoContent {
	id: number;
	text: string;
	thumbNailUrl: string;
	start: number;
	end: number;
	profanity: number;
	sentiment: VideoContentSentiment;
}

export interface VideoContentSentiment {
	pos: number;
	neu: number;
	neg: number;
}

export interface VideoFilter {
	data: Video[];
	metaData: Array<{
		total: number;
		page: number;
	}>;
}

export interface ISentiment {
	entry: number;
	negative: number;
	neutral?: number;
	positive: number;
	text: string;
	negativeColor?: string;
	neutralColor?: string;
	positiveColor?: string;
}

// OpenSearch document structure
export interface OpenSearchVideo {
	id: string;
	content: {
		// Video metadata
		video_id?: string;
		channel_name?: string;
		channel_url?: string;
		video_title?: string;
		video_url?: string;
		video_description?: string;
		thumbnail_url?: string;
		platform?: string;
		duration?: number;
		
		// Content
		transcription?: string;
		searchTranscription?: string;
		summary?: string;
		searchSummary?: string;
		
		// Dates
		published_date?: string;
		searchDate?: string;
		
		// Stats (with both naming conventions)
		views?: number;
		likes?: number;
		comments?: number;
		dislikes?: number;
		statsViews?: number;
		statsLikes?: number;
		statsComments?: number;
		statsDislikes?: number;
		
		// Search data
		searchKeywords?: string[];
		searchEntities?: string[];
		searchHashtags?: string[];
		searchMentions?: string[];
		searchTopics?: string[];
		
		// Sentiment
		sentimentPositive?: number;
		sentimentNegative?: number;
		sentimentNeutral?: number;
		
		// Creator stats
		creatorFollowers?: number;
		creatorFollowing?: number;
		creatorPosts?: number;
		
		// Embeddings
		transcription_embedding?: number[];
		summary_embedding?: number[];
		text_embedding?: number[];
		
		[key: string]: any;
	};
	score?: number;
}


// Convert OpenSearch document to Video format using rawData directly
export const convertOpenSearchToVideo = (doc: OpenSearchVideo): Video => {
	// Use rawData directly since it has the same structure as Video interface
	const rawData = doc.content.rawData;
	
	if (!rawData) {
		// Fallback if rawData is not available - use search* fields from doc.content
		return {
			_id: doc.id,
			metaData: {
				id: doc.id,
				creatorId: '',
				creatorName: '',
				creatorSub: '0',
				creatorUrl: '',
				contentType: '',
				duration: '0',
				stats: { views: 0, likes: 0, dislikes: 0, comments: 0 },
				title: '',
				description: '',
				date: doc.content.searchDate || '',
				thumbnail: '',
				url: '',
				platform: '',
				hashtags: [],
				brandsMentioned: [],
				productsMentioned: [],
				iabClassification: [],
				sponsored: false,
				sponsors: [],
				keyWords: doc.content.searchKeywords || [],
				entities: doc.content.searchEntities || [],
				topics: [],
				links: [],
				summary: doc.content.searchSummary || '',
				comprehension: 8.5,
				text: '',
				searchTranscription: doc.content.searchTranscription || '',
				searchTerm: '',
				segments: 0,
				SentimentSummary: { pos: 0.7, neu: 0.2, neg: 0.1 },
			},
			content: [],
		};
	}

	return {
		_id: doc.id,
		metaData: {
			id: doc.id,
			creatorId: rawData.metaData?.creatorId || 'Unknown',
			creatorName: Array.isArray(rawData.metaData?.creatorName) 
				? rawData.metaData.creatorName[0] 
				: rawData.metaData?.creatorName || 'Unknown Creator',
            creatorSub: rawData.creatorData?.[0]?.platforms?.Youtube?.connections?.subscribers || 
                       rawData.creatorData?.[0]?.platforms?.Instagram?.connections?.subscribers ||
                       rawData.creatorData?.[0]?.platforms?.Tiktok?.connections?.subscribers || '0',
			creatorUrl: rawData.metaData?.creatorUrl || '',
			contentType: rawData.metaData?.contentType || '',
			duration: rawData.metaData?.duration || '0',
			stats: {
				views: rawData.metaData?.stats?.views || 0,
				likes: rawData.metaData?.stats?.likes || 0,
				dislikes: rawData.metaData?.stats?.dislikes || 0,
				comments: rawData.metaData?.stats?.comments || 0,
			},
			title: rawData.metaData?.title || 'No title available',
			description: rawData.metaData?.description || 'No description available',
			date: doc.content.searchDate || rawData.metaData?.date || '',
			thumbnail: rawData.metaData?.thumbnail || '',
			url: rawData.metaData?.url || '',
			platform: rawData.metaData?.platform || 'Unknown',
			hashtags: rawData.metaData?.hashtags || [],
			brandsMentioned: [],
			productsMentioned: [],
			iabClassification: [],
			sponsored: false,
			sponsors: [],
			keyWords: doc.content.searchKeywords || rawData.metaData?.keywords || [],
			entities: doc.content.searchEntities || [],
			topics: [],
			links: rawData.metaData?.links || [],
			summary: doc.content.searchSummary || '',
			comprehension: 8.5, // Default to "average" level since comprehension data not available in OpenSearch
			text: '',
			searchTranscription: doc.content.searchTranscription || '',
			searchTerm: '',
			segments: rawData.metaData?.segments || 0,
			SentimentSummary: rawData.metaData?.sentiment ? {
				pos: parseFloat(rawData.metaData.sentiment.positive || '0'),
				neu: parseFloat(rawData.metaData.sentiment.neutral || '0'),
				neg: parseFloat(rawData.metaData.sentiment.negative || '0')
			} : { pos: 0.7, neu: 0.2, neg: 0.1 }, // Temporary fallback until real sentiment data is available
		},
	content: (rawData.transcription?.baseTranscription?.segments || []).map((segment: any, index: number) => ({
		id: segment.id || index,
		text: segment.text || '',
		thumbNailUrl: '',
		start: segment.start || 0,
		end: segment.end || 0,
		profanity: 0,
		sentiment: { pos: 0, neu: 1, neg: 0 } // Default neutral sentiment
	})),
	creatorData: rawData.creatorData || []
	};
};
